import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import TagLinkButton from 'flarum/tags/components/TagLinkButton';

const STORAGE_KEY = 'roglo-tag-filter-excluded';

let initialRefreshDone = false;
let refreshInProgress = false;
let refreshPending = false;


/*
 * ------------------------------------------------------------------
 * Gestion des slugs
 * ------------------------------------------------------------------
 */

function normalizeSlug(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const slug = value.trim();

    return slug !== '' ? slug : null;
}


function normalizeSlugs(values) {
    if (!Array.isArray(values)) {
        return [];
    }

    const result = [];

    values.forEach((value) => {
        const slug = normalizeSlug(value);

        if (
            slug !== null &&
            result.indexOf(slug) === -1
        ) {
            result.push(slug);
        }
    });

    return result;
}


/*
 * ------------------------------------------------------------------
 * Configuration par défaut
 * ------------------------------------------------------------------
 */

function getDefaultExcludedTags() {
    const raw = app.forum.attribute(
        'rogloTagFilterDefaultExcludedTags'
    );

    if (typeof raw !== 'string') {
        return [];
    }

    return normalizeSlugs(raw.split(','));
}


/*
 * ------------------------------------------------------------------
 * Préférences locales
 * ------------------------------------------------------------------
 */

function getExcludedTags() {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (raw === null) {
        return getDefaultExcludedTags();
    }

    try {
        const parsed = JSON.parse(raw);

        if (!Array.isArray(parsed)) {
            return getDefaultExcludedTags();
        }

        for (let i = 0; i < parsed.length; i++) {
            if (typeof parsed[i] !== 'string') {
                return getDefaultExcludedTags();
            }
        }

        return normalizeSlugs(parsed);

    } catch (error) {
        console.error(
            'RogloTagFilter: erreur localStorage',
            error
        );

        return getDefaultExcludedTags();
    }
}


function saveExcludedTags(values) {
    window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(normalizeSlugs(values))
    );
}


/*
 * ------------------------------------------------------------------
 * État des étiquettes
 * ------------------------------------------------------------------
 */

function isTagEnabled(slug) {
    return getExcludedTags().indexOf(slug) === -1;
}


function toggleTag(slug) {
    slug = normalizeSlug(slug);

    if (slug === null) {
        return;
    }

    const excluded = getExcludedTags();
    const position = excluded.indexOf(slug);

    if (position === -1) {
        excluded.push(slug);
    } else {
        excluded.splice(position, 1);
    }

    saveExcludedTags(excluded);
}


/*
 * ------------------------------------------------------------------
 * Toutes les étiquettes disponibles
 * ------------------------------------------------------------------
 */

function getAllTagSlugs() {
    return normalizeSlugs(
        app.store
            .all('tags')
            .map((tag) => tag.slug())
    );
}


/*
 * ------------------------------------------------------------------
 * État de la case générale
 * ------------------------------------------------------------------
 */

function getAllTagsState() {
    const slugs = getAllTagSlugs();
    const excluded = getExcludedTags();

    if (slugs.length === 0) {
        return {
            checked: false,
            indeterminate: false,
            disabled: true
        };
    }

    let enabledCount = 0;

    slugs.forEach((slug) => {
        if (excluded.indexOf(slug) === -1) {
            enabledCount++;
        }
    });

    return {
        checked: enabledCount === slugs.length,
        indeterminate:
            enabledCount > 0 &&
            enabledCount < slugs.length,
        disabled: false
    };
}


/*
 * ------------------------------------------------------------------
 * Tout cocher / Tout décocher
 * ------------------------------------------------------------------
 */

function setAllTagsEnabled(enabled) {
    const slugs = getAllTagSlugs();

    if (slugs.length === 0) {
        return;
    }

    const currentExcluded = getExcludedTags();

    /*
     * On conserve les exclusions éventuelles correspondant
     * à des étiquettes absentes du store courant.
     */
    const unknownExcluded = currentExcluded.filter(
        (slug) => slugs.indexOf(slug) === -1
    );

    if (enabled) {
        saveExcludedTags(unknownExcluded);
    } else {
        saveExcludedTags(
            unknownExcluded.concat(slugs)
        );
    }
}


/*
 * ------------------------------------------------------------------
 * Ajout de excludeTags aux requêtes /api/discussions
 * ------------------------------------------------------------------
 */

function applyDiscussionFilter(params) {
    if (!params || typeof params !== 'object') {
        return;
    }

    const excluded = getExcludedTags();

    if (!params.filter) {
        params.filter = {};
    }

    if (excluded.length > 0) {
        params.filter.excludeTags = excluded.join(',');
    } else {
        delete params.filter.excludeTags;
    }
}


/*
 * ------------------------------------------------------------------
 * Rechargement des discussions
 * ------------------------------------------------------------------
 *
 * Une modification effectuée pendant un chargement
 * déclenche un second rafraîchissement à la fin.
 * ------------------------------------------------------------------
 */

function refreshDiscussionList() {
    if (
        !app.discussions ||
        typeof app.discussions.refresh !== 'function'
    ) {
        return Promise.resolve();
    }

    if (refreshInProgress) {
        refreshPending = true;
        return Promise.resolve();
    }

    refreshInProgress = true;

    return app.discussions
        .refresh(1)
        .catch((error) => {
            console.error(
                'RogloTagFilter: erreur lors du rechargement des discussions',
                error
            );
        })
        .finally(() => {
            refreshInProgress = false;

            if (refreshPending) {
                refreshPending = false;
                refreshDiscussionList();
            }
        });
}


/*
 * ==================================================================
 * INITIALISATION
 * ==================================================================
 */

app.initializers.add('roglo-tag-filter', () => {

    /*
     * --------------------------------------------------------------
     * 1. Filtre API
     * --------------------------------------------------------------
     */

    extend(
        DiscussionListState.prototype,
        'requestParams',
        function (params) {
            applyDiscussionFilter(params);
        }
    );


    /*
     * --------------------------------------------------------------
     * 2. Cases individuelles des étiquettes
     * --------------------------------------------------------------
     */

    extend(
        TagLinkButton.prototype,
        'view',
        function (vnode) {

            const tag = this.attrs.model;

            if (!tag || !vnode) {
                return;
            }

            let slug = null;

            if (
                tag.data &&
                tag.data.attributes &&
                typeof tag.data.attributes.slug === 'string'
            ) {
                slug = tag.data.attributes.slug;
            }

            if (
                slug === null &&
                typeof tag.slug === 'function'
            ) {
                const candidate = tag.slug();

                if (typeof candidate === 'string') {
                    slug = candidate;
                }
            }

            slug = normalizeSlug(slug);

            if (slug === null) {
                return;
            }

            const enabled = isTagEnabled(slug);

            let children = vnode.children;

            if (!Array.isArray(children)) {
                if (
                    children === undefined ||
                    children === null
                ) {
                    children = [];
                } else {
                    children = [children];
                }
            }

            const alreadyPresent = children.some(
                (child) =>
                    child &&
                    child.attrs &&
                    child.attrs.className ===
                        'RogloTagFilter-checkbox'
            );

            if (alreadyPresent) {
                return;
            }

            children.unshift(
                m('input', {
                    type: 'checkbox',

                    className: 'RogloTagFilter-checkbox',

                    checked: enabled,

                    'data-roglo-tag-slug': slug,

                    title: enabled
                        ? 'Étiquette affichée'
                        : 'Étiquette masquée',

                    onclick: (event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        toggleTag(slug);
                        refreshDiscussionList();
                    }
                })
            );

            vnode.children = children;
        }
    );


    /*
     * --------------------------------------------------------------
     * 3. Menu latéral des étiquettes
     * --------------------------------------------------------------
     */

    extend(
        IndexPage.prototype,
        'navItems',
        function (items) {

            const tags = app.store.all('tags');
            const params = app.search.stickyParams();
            const currentTag = this.currentTag();

            /*
             * Afficher toutes les étiquettes non positionnées.
             */
            tags
                .filter(
                    (tag) => tag.position() === null
                )
                .sort(
                    (a, b) =>
                        b.discussionCount() -
                        a.discussionCount()
                )
                .forEach((tag) => {

                    const key = 'tag' + tag.id();

                    if (items.has(key)) {
                        return;
                    }

                    let active = currentTag === tag;

                    if (!active && currentTag) {
                        active = currentTag.parent() === tag;
                    }

                    items.add(
                        key,

                        <TagLinkButton
                            model={tag}
                            params={params}
                            active={active}
                        >
                            {tag.name()}
                        </TagLinkButton>,

                        -15
                    );
                });

            /*
             * Supprimer le lien "Plus..."
             */
            if (items.has('moreTags')) {
                items.remove('moreTags');
            }


            /*
             * ------------------------------------------------------
             * Case générale
             * ------------------------------------------------------
             */

            const state = getAllTagsState();

            items.add(
                'rogloTagFilterAll',

                <div className="RogloTagFilter-all">
                    <label className="RogloTagFilter-allLabel">

                        <input
                            type="checkbox"
                            className="RogloTagFilter-allCheckbox"

                            checked={state.checked}
                            disabled={state.disabled}

                            oncreate={(vnode) => {
                                vnode.dom.indeterminate =
                                    state.indeterminate;
                            }}

                            onupdate={(vnode) => {
                                vnode.dom.indeterminate =
                                    state.indeterminate;
                            }}

                            onclick={(event) => {
                                event.stopPropagation();

                                setAllTagsEnabled(
                                    event.target.checked
                                );

                                refreshDiscussionList();
                            }}
                        />

                        <span>
                            Tout cocher / Tout décocher
                        </span>

                    </label>
                </div>,

                -13
            );
        }
    );


    /*
     * --------------------------------------------------------------
     * 4. Remplacement du preload initial
     * --------------------------------------------------------------
     */

    extend(
        IndexPage.prototype,
        'oncreate',
        function () {

            if (initialRefreshDone) {
                return;
            }

            initialRefreshDone = true;

            window.setTimeout(
                () => {
                    refreshDiscussionList();
                },
                0
            );
        }
    );
});
